import { motion } from "framer-motion";
import {
  Lightning,
  Heartbeat,
  Calculator,
  Books,
  Clock,
  ShieldCheck,
} from "@phosphor-icons/react";
import { ASSETS } from "@/lib/constants";

const NURSE_PHONE = ASSETS.nursePhone;

const FEATURES = [
  {
    id: "F-01",
    title: "Rapid recall under pressure",
    desc: "50+ clinical tools in one app. Ask in plain language, get the dose, the algorithm, the differential — in seconds.",
    icon: <Lightning size={22} weight="duotone" />,
    span: "md:col-span-7 md:row-span-2",
    image: true,
  },
  {
    id: "F-02",
    title: "ECG interpretation + drug-drug interactions",
    desc: "Snap an ECG for a rhythm read. Check any two drugs for clinically-relevant interactions before you push. Both built for bedside speed.",
    icon: <Heartbeat size={22} weight="duotone" />,
    span: "md:col-span-5",
  },
  {
    id: "F-03",
    title: "Bedside calculators & scoring",
    desc: "Weight-based Peds Dose Calc, Parkland burn fluid, COWS & alcohol withdrawal, GCS, CrCl, anion gap, sodium/potassium corrections, IBW/ABW. All grounded in current guidelines.",
    icon: <Calculator size={22} weight="duotone" />,
    span: "md:col-span-5",
  },
  {
    id: "F-04",
    title: "Source-cited answers",
    desc: "Anchored to standardized critical care protocols — ACLS, PALS, ATLS, SCCM — plus published clinical guidelines, peer-reviewed studies, and authoritative references. Every reply ships with its source cited inline.",
    icon: <Books size={22} weight="duotone" />,
    span: "md:col-span-4",
  },
  {
    id: "F-05",
    title: "Shift-aware history",
    desc: "Your question history, saved and searchable. Bookmark the answers you'll need again — pick up where you left at handoff.",
    icon: <Clock size={22} weight="duotone" />,
    span: "md:col-span-4",
  },
  {
    id: "F-06",
    title: "Privacy-first by design",
    desc: "No login, no account, no patient identifiers. Questions are designed to be phrased generically, and built-in helpers prompt you to keep PHI out. Conversations stay on-device whenever possible.",
    icon: <ShieldCheck size={22} weight="duotone" />,
    span: "md:col-span-4",
  },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-28 md:py-36 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
              § 01 · capabilities
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter max-w-3xl leading-[0.95]">
              The clinical brain
              <br />
              in your <span className="italic font-light">scrub pocket.</span>
            </h2>
          </div>
          <p className="md:max-w-sm text-base text-white/55 leading-relaxed">
            Designed around the questions you actually whisper at 3 a.m. — not
            the ones the textbooks were written for.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10">
          {FEATURES.map((f, idx) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              className={`group relative bg-black p-8 md:p-10 hover:bg-[#0a0a0a] transition-colors min-h-[260px] flex flex-col justify-between overflow-hidden ${f.span}`}
              data-testid={`feature-card-${f.id}`}
            >
              {f.image && (
                <div className="absolute inset-0 -z-0 opacity-30 group-hover:opacity-50 transition-opacity">
                  <img
                    src={NURSE_PHONE}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black" />
                </div>
              )}
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 grid place-items-center bg-[#FF2A2A]/10 border border-[#FF2A2A]/40 text-[#FF2A2A]">
                    {f.icon}
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                    {f.id}
                  </span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight leading-tight mb-3 max-w-md">
                  {f.title}
                </h3>
                <p className="text-sm md:text-[15px] text-white/55 leading-relaxed max-w-md">
                  {f.desc}
                </p>
              </div>
              <div
                className="absolute bottom-0 left-0 right-0 h-px bg-[#FF2A2A] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                aria-hidden="true"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
